/**
 * @file        src/lib/fareBreakdown.js
 * @description Shared flight fare-breakdown arithmetic. `baggageTotal()` /
 *              `addonCount()` sum a per-segment, per-passenger SSR selection
 *              ({ [segmentId]: { [paxIdx]: { amount, ... } } }) — the shape
 *              both excess-baggage and meal picks use. Consolidates five
 *              byte-for-byte-identical local copies (FlightBook, FlightReview,
 *              FlightPayment, Confirmation, VoucherModal all defined the exact
 *              same loop under the same name) into one source, so a fix can
 *              never land in only one of them again.
 *
 *              `buildPassengerBreakdown()` (CR B5) re-groups the SAME seat/meal
 *              selection objects (`seatBySeg`, `mealSel` from booking.store.js)
 *              by PASSENGER instead of by segment, for the Passenger & Contact
 *              step's per-passenger seat/meal price breakdown. It is a pure
 *              regrouping — no new arithmetic, no change to what a seat/meal
 *              costs — so a sum over its `subtotal`s always equals
 *              `baggageTotal(seatBySeg) + baggageTotal(mealSel)`.
 *
 *              `computeConvenienceFee()` / `bestFlightDiscount()` /
 *              `computeCouponDiscount()` / `combineDiscounts()` (CR B1) are the
 *              coupon/discount arithmetic extracted verbatim from
 *              FlightPayment (the only step that used to compute it) so
 *              FlightReview/FlightBook can preview the SAME applied coupon with
 *              the SAME math, instead of a second parallel implementation.
 *              Byte-for-byte behaviour preserved — see FlightPayment's
 *              pre-2026-08-04 `bestFlightDiscount`/`computeCoupon` for the
 *              originals this replaces.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-10-08 Claude — Infants pay the flat convenience fee too (client
 *                          decision): payingPaxOf counts adults + children +
 *                          infants. Mirrors flightPricingService.payingPaxOf.
 *   - 2026-10-07 Claude — A flat convenience fee is PER PAYING PASSENGER (adults
 *                          + children, not infants): computeConvenienceFee takes
 *                          `payingPax`, priceFlightCheckout passes it through,
 *                          payingPaxOf() / convenienceFeeLabel() added. Mirrors
 *                          booking_backend flightPricingService — keep in step.
 *   - 2026-10-06 Claude — Domestic / International scope (admin migration 076):
 *                          tripLegsFor puts each leg's `domInt` on it (from the
 *                          card's `_raw.sI` countries), ruleFitsTrip holds a
 *                          scoped rule to the WHOLE trip, and domIntMiss() names
 *                          the half a refused coupon belongs to. Mirrors
 *                          booking_backend/utils/domIntScope.js — keep in step.
 *   - 2026-10-05 Claude — Airline Type / Airline Code / Fare Type scope on the
 *                          coupon, auto discount and convenience fee (admin
 *                          migration 074): ruleFitsTrip / pickConvenienceFee /
 *                          tripLegsFor mirror booking_backend's
 *                          utils/flightRuleScope.js + flightPricingService, so
 *                          the preview applies a scoped rule exactly where the
 *                          checkout will. bestFlightDiscount and
 *                          priceFlightCheckout take the chosen `legs`.
 *   - 2026-10-05 Claude — Added priceFlightCheckout() + discountRuleLabel(): the
 *                          fee/discount/coupon composition FlightPayment used to
 *                          do inline, now shared so Passenger Details and Review
 *                          show the same auto discount + convenience fee.
 *   - 2026-08-04 Claude — CR B1: added round2/airlineOf/computeConvenienceFee/
 *                          bestFlightDiscount/computeCouponDiscount/
 *                          combineDiscounts, moved here from FlightPayment so
 *                          FlightReview/FlightBook can render the same applied
 *                          coupon consistently via the shared <PriceSummary>
 *                          instead of re-deriving it. No arithmetic changed.
 *   - 2026-08-04 Claude — CR B3: extracted from the five duplicate
 *                          `baggageTotal()` implementations (verified
 *                          identical behaviour — no drift found) and the one
 *                          `addonCount()` implementation in FlightBook (the
 *                          other call sites re-derived the same count inline).
 *   - 2026-08-04 Claude — CR B5: added `buildPassengerBreakdown()` — groups
 *                          seat + meal picks per passenger (name, each pick +
 *                          its price, subtotal) for FlightBook's new
 *                          "Price breakdown by passenger" panel.
 *   - 2026-08-31 Claude — Added `splitBaseTaxes()`, extracted verbatim from
 *                          FlightBook's `PriceDetails` (base fare / taxes &
 *                          fees estimate for the pre-payment steps, where the
 *                          store's `fare` only ever carries a total `price`,
 *                          never TripJack's own base/tax split). FlightReview
 *                          now uses it too, so its sidebar shows the same Base
 *                          fare / Taxes & fees rows as FlightBook instead of a
 *                          single opaque "Tickets" line — same formula, so the
 *                          two screens can never disagree on the split.
 *   - 2026-09-07 Claude — `splitBaseTaxes()` now takes `selectedTrip` too and
 *                          returns the Processing fee as its OWN line, derived
 *                          from the supplier price the way FlightPayment already
 *                          did (processing = price − supplierPrice, taxes =
 *                          supplierPrice − baseFare). Steps 1 and 2 used to show
 *                          a 55/45 GUESS labelled "Base fare / Taxes & fees"
 *                          with our markup silently buried inside taxes — so the
 *                          search card's Fare Details (₹1,009 + ₹1,466.50 + ₹300
 *                          processing) and the checkout sidebar (₹1,527 +
 *                          ₹1,249) described the same ₹2,775.50 ticket with
 *                          different numbers. All three steps now share this one
 *                          function, so they cannot drift again.
 */

/**
 * Sum every chosen SSR tier's price across all segments + passengers. Used for
 * both excess-baggage and meal selections — same shape, same sum. A free pick
 * (amount 0) still counts toward `addonCount()`, so never treat a zero total as
 * "nothing chosen".
 * @param {Object<string, Object<string, { amount?: number|string }>>} sel
 * @returns {number}
 */
export function baggageTotal(sel) {
  let total = 0;
  for (const seg of Object.values(sel || {})) {
    for (const pick of Object.values(seg || {})) total += Number(pick?.amount) || 0;
  }
  return total;
}

/**
 * How many (segment, passenger) picks exist in a selection — the honest
 * "is anything chosen" test, since `baggageTotal()` can legitimately be zero
 * (free special meals, a zero-priced baggage tier).
 * @param {Object<string, Object<string, unknown>>} sel
 * @returns {number}
 */
export function addonCount(sel) {
  return Object.values(sel || {}).reduce((n, seg) => n + Object.keys(seg || {}).length, 0);
}

/**
 * Re-group the store's per-SEGMENT seat/meal selections into one entry per
 * PASSENGER — the shape the "Price breakdown by passenger" panel renders.
 * Pure regrouping of the exact same picks the segment-keyed totals above sum,
 * so this can never disagree with the grand total: it does not re-price
 * anything, only re-indexes it.
 *
 * `seatBySeg` entries carry the price under `price` (booking.store.js
 * `selectSeatFor`); `mealSel` entries carry it under `amount` (the raw SSR
 * option spread by `selectMealFor`) — both are read here, never renamed, so a
 * consumer can trace a figure straight back to the store slice it came from.
 *
 * @param {Object} args
 * @param {Array<{type:string,label:string}>} args.paxSlots - full passenger
 *   list in booking order (index = paxIndex the selections are keyed by).
 * @param {Object<string, Object<string, {id?:string,price?:number,from?:string,to?:string}>>} args.seatBySeg
 * @param {Object<string, Object<string, {code?:string,amount?:number,desc?:string,from?:string,to?:string}>>} args.mealSel
 * @returns {Array<{idx:number,label:string,seats:Array,meals:Array,subtotal:number}>}
 *   One entry per passenger who has at least one seat or meal pick, sorted by
 *   passenger index (booking order). A passenger with nothing selected is
 *   simply absent — an empty "Adult 2 — no extras" row would only add noise.
 */
export function buildPassengerBreakdown({ paxSlots, seatBySeg, mealSel }) {
  const byPax = new Map();
  const entryFor = (idx) => {
    if (!byPax.has(idx)) {
      byPax.set(idx, {
        idx,
        label: paxSlots?.[idx]?.label || `Passenger ${idx + 1}`,
        seats: [],
        meals: [],
        subtotal: 0,
      });
    }
    return byPax.get(idx);
  };

  for (const [segKey, seg] of Object.entries(seatBySeg || {})) {
    for (const [paxIdxStr, pick] of Object.entries(seg || {})) {
      if (!pick) continue;
      const idx = Number(paxIdxStr);
      const price = Number(pick.price) || 0;
      const entry = entryFor(idx);
      entry.seats.push({ segKey, id: pick.id, from: pick.from || '', to: pick.to || '', price });
      entry.subtotal += price;
    }
  }

  for (const [segmentId, seg] of Object.entries(mealSel || {})) {
    for (const [paxIdxStr, pick] of Object.entries(seg || {})) {
      if (!pick) continue;
      const idx = Number(paxIdxStr);
      const price = Number(pick.amount) || 0;
      const entry = entryFor(idx);
      entry.meals.push({ segmentId, desc: pick.desc || '', from: pick.from || '', to: pick.to || '', price });
      entry.subtotal += price;
    }
  }

  return [...byPax.values()].sort((a, b) => a.idx - b.idx);
}

/**
 * Split a ticket price into Base fare + Taxes & fees + Processing fee (OUR
 * markup) — the SAME three lines the search card's Fare Details table shows, so
 * the breakdown never changes shape between the results page and checkout.
 *
 * `fare` is the store's `fare`/`selectedFare` (carries the marked-up total as
 * `price`); `trip` is `selectedTrip`, which carries the supplier's own figures
 * (`baseFare` = TripJack BF, `supplierPrice` = TripJack TF, what we owe the
 * airline). Derived exactly as FlightPayment has always derived them:
 *
 *   Processing fee = price − supplierPrice    (our REAL margin-rule markup)
 *   Taxes & fees   = supplierPrice − baseFare (the FULL airline taxes)
 *
 * Never derive taxes as `price − base` and processing as the residual: when the
 * stored taxes figure under-counts the supplier's, the shortfall leaks into
 * "Processing fee" and inflates it far above our actual margin.
 *
 * Falls back to a 55/45 base/tax estimate for whichever figure the supplier has
 * not given us (no `baseFare` yet, or no `supplierPrice` — in which case there
 * is no markup to show and the processing line is 0). By construction
 * `baseFare + taxesFees + processingFee === price` in every branch, so the rows
 * always add back up to the ticket price shown beside them.
 *
 * @param {{price?:number}} fare - store `fare` / `selectedFare`
 * @param {{baseFare?:number, supplierPrice?:number}} [trip] - store `selectedTrip`
 * @returns {{baseFare:number, taxesFees:number, processingFee:number}}
 */
export function splitBaseTaxes(fare, trip) {
  const price = round2(fare?.price);
  // Supplier total (TF). Absent → assume no markup, so no processing line.
  const supplier = Number(trip?.supplierPrice) || price;
  const processingFee = Math.max(0, round2(price - supplier));
  // The supplier's share of what the customer pays — price minus our markup.
  // Taking it back off `price` (rather than using `supplier` directly) keeps the
  // three rows summing to `price` even in the degenerate supplier > price case.
  const ticket = round2(price - processingFee);
  // The supplier figure verbatim when we have it (it can carry paise); the
  // estimate stays whole-rupee — decimals on a guess only look authoritative.
  const baseFare = Number(trip?.baseFare) ? round2(trip.baseFare) : Math.round(ticket * 0.55);
  const taxesFees = Math.max(0, round2(ticket - baseFare));
  return { baseFare, taxesFees, processingFee };
}

/** Round to 2dp, coercing null/NaN to 0. Every rupee figure below goes through this. */
export function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Marketing airline (2-char IATA) from a card code like "AI2977" / "6E1234". */
export function airlineOf(code) {
  return String(code || '').toUpperCase().match(/^[A-Z0-9]{2}/)?.[0] || '';
}

/**
 * Convenience-fee amount for `base` under the active admin rule (as returned
 * by GET /flight-offers → `convenienceFee`). 0 when no rule, or `base` is
 * outside the rule's min/max band.
 * A FLAT fee is charged PER PASSENGER — adults, children AND infants (client
 * decisions 2026-10-07 / 2026-10-08). A PERCENTAGE fee is taken on `base`,
 * which already covers every passenger, so it is not multiplied. The band is
 * judged on the whole `base`. Mirrors booking_backend flightPricingService.
 * @param {{feeType:string,feeValue:number,minAmount?:number,maxAmount?:number}|null} feeCfg
 * @param {number} base
 * @param {number} [payingPax=1]  every passenger (payingPaxOf)
 */
export function computeConvenienceFee(feeCfg, base, payingPax = 1) {
  if (!feeCfg) return 0;
  const b = Number(base) || 0;
  if (b < Number(feeCfg.minAmount || 0)) return 0;
  if (feeCfg.maxAmount != null && b > Number(feeCfg.maxAmount)) return 0;
  const n = Math.max(1, Math.floor(Number(payingPax) || 1));
  const amt = feeCfg.feeType === 'percentage' ? (b * Number(feeCfg.feeValue)) / 100 : Number(feeCfg.feeValue) * n;
  return round2(Math.max(0, amt));
}

/**
 * Passengers the convenience fee is charged on: adults + children + infants
 * from the flight search (the mix the fare was priced for — the server refuses
 * any other with PAX_MISMATCH). Every traveller pays it, an infant included
 * (client decision 2026-10-08). Never below 1.
 */
export function payingPaxOf(search) {
  return Math.max(1, Number(search?.adults) || 1)
    + Math.max(0, Number(search?.children) || 0)
    + Math.max(0, Number(search?.infants) || 0);
}

/**
 * The price panel's label for the fee line: "Convenience fee (₹200 × 2)" for a
 * flat fee on more than one passenger, so a doubled figure explains itself.
 * `inr` is passed in so this module stays free of the formatting import.
 */
export function convenienceFeeLabel(feeCfg, payingPax, inr) {
  const name = feeCfg?.name || 'Convenience fee';
  const n = Math.max(1, Math.floor(Number(payingPax) || 1));
  if (!feeCfg || feeCfg.feeType === 'percentage' || n < 2) return name;
  return `${name} (${inr(Number(feeCfg.feeValue))} × ${n})`;
}

/**
 * Best-matching ADMIN auto flight-discount for `base` on `airlineCode`, from
 * the rule list GET /flight-offers → `flightDiscounts` returns. Mirrors the
 * server's flightPricingService so the preview matches the eventual charge.
 * Returns BOTH the amount and the winning rule (so the UI can label it "10%
 * off" / "₹1,000 flat"), never just the value.
 * @param {Array} rules
 * @param {number} base
 * @param {string} airlineCode
 * @returns {{amount:number, rule:object|null}}
 */
export function bestFlightDiscount(rules, base, airlineCode, legs = null) {
  // No legs ⇒ the trip is just `airlineCode` with an unknown fare family, so a
  // fare-scoped rule cannot fit — the server's own fallback, identically.
  const trip = Array.isArray(legs) && legs.length ? legs : [{ airlineCode: airlineOf(airlineCode), fareIdentifier: '' }];
  let best = { amount: 0, rule: null };
  for (const d of (rules || [])) {
    if (base < Number(d.minBookingAmount || 0)) continue;
    if (!ruleFitsTrip(d, trip)) continue;
    let amt = d.discountType === 'percentage' ? (base * Number(d.discountValue)) / 100 : Number(d.discountValue);
    if (d.maxDiscount != null) amt = Math.min(amt, Number(d.maxDiscount));
    amt = round2(Math.max(0, amt));
    if (amt > best.amount) best = { amount: amt, rule: d };
  }
  return best;
}

/**
 * Discount for a CUSTOMER coupon — booking.store.js `appliedCoupon`
 * ({ discountType, discountValue }) or a raw `/flight-offers` coupons[] entry
 * (same camelCase shape) — against `base`. 0 for a falsy/invalid row.
 * @param {{discountType?:string, discountValue?:number}|null} row
 * @param {number} base
 */
export function computeCouponDiscount(row, base) {
  if (!row) return 0;
  const b = Number(base) || 0;
  const amt = row.discountType === 'percentage' ? (b * Number(row.discountValue)) / 100 : Number(row.discountValue);
  return round2(Math.max(0, amt));
}

/**
 * Stack an auto flight-discount + a customer coupon on the same `base`: BOTH
 * apply in full unless their sum would exceed `base` (a discount can never
 * take the fare negative), in which case the coupon is trimmed first, then
 * the auto discount. Mirrors easebuzzFlightController's server-side stacking
 * so every preview agrees with the authoritative charge.
 * @param {number} flightDiscountAmt
 * @param {number} couponDiscountAmt
 * @param {number} base
 * @returns {{flightDiscount:number, couponDiscount:number, totalDiscount:number}}
 */
export function combineDiscounts(flightDiscountAmt, couponDiscountAmt, base) {
  const b = Number(base) || 0;
  let couponDiscount = Number(couponDiscountAmt) || 0;
  let flightDiscount = Number(flightDiscountAmt) || 0;
  if (round2(flightDiscount + couponDiscount) > b) {
    couponDiscount = Math.min(couponDiscount, b);
    flightDiscount = Math.max(0, round2(b - couponDiscount));
  }
  return { flightDiscount, couponDiscount, totalDiscount: round2(couponDiscount + flightDiscount) };
}

/**
 * Human label for a discount rule: "10% off" for a percentage rule, "₹1,000
 * flat" for a fixed one — printed beside the Discount line. `inr` is passed in
 * so this module stays free of the formatting import.
 */
export function discountRuleLabel(rule, inr) {
  if (!rule) return '';
  return String(rule.discountType) === 'percentage'
    ? `${Number(rule.discountValue)}% off`
    : `${inr(Number(rule.discountValue))} flat`;
}

/**
 * The fee / discount / coupon half of a flight checkout total — the ONE
 * composition every step's price panel renders (FlightBook, FlightReview,
 * FlightPayment), so the discount shown before payment is the discount charged
 * at it. Same order the server applies (flightCheckoutController):
 *   convenience fee on the ticket + add-on subtotal;
 *   auto discount + coupon on subtotal + fee, stacked (combineDiscounts);
 *   insurance on top, never discounted.
 * @param {{subtotal:number, insurance?:number, coupon?:object|null,
 *          offers?:{convenienceFee?:object|null, convenienceFees?:Array, flightDiscounts?:Array},
 *          airlineCode?:string, legs?:Array}} p
 *   `legs` — tripLegsFor(selectedLegs, selectedTrip): judges each rule's
 *   Airline / Fare Type scope. A coupon that does not fit the trip takes nothing
 *   off here, because the server will not take it off at initiate either.
 */
export function priceFlightCheckout({ subtotal, insurance = 0, coupon = null, offers = null, airlineCode = '', legs = null, payingPax = 1 }) {
  const trip = Array.isArray(legs) && legs.length ? legs : [{ airlineCode: airlineOf(airlineCode), fareIdentifier: '' }];
  const feeCfg = pickConvenienceFee(offers, trip);
  const convenienceFee = computeConvenienceFee(feeCfg, subtotal, payingPax);
  const discountBase = round2(subtotal + convenienceFee);
  const best = bestFlightDiscount(offers?.flightDiscounts, discountBase, airlineCode, trip);
  const rawCoupon = coupon && ruleFitsTrip(coupon, trip) ? computeCouponDiscount(coupon, discountBase) : 0;
  const { flightDiscount, couponDiscount, totalDiscount } = combineDiscounts(best.amount, rawCoupon, discountBase);
  return {
    feeCfg,
    convenienceFee,
    discountBase,
    discountRule: best.rule,
    flightDiscount,
    couponDiscount,
    totalDiscount,
    grand: Math.max(1, round2(subtotal + convenienceFee - totalDiscount + (Number(insurance) || 0))),
  };
}

// ── Airline / Fare Type scope ─────────────────────────────────────────────────
// Mirrors booking_backend/utils/flightRuleScope.js — the server is the boundary
// (it recomputes every rupee at initiate); this exists so the preview applies a
// scoped coupon, discount or convenience fee exactly where the charge will.
// Keep the two in step.

// A Fare Type is picked in the admin by the name this site prints, so it is
// matched by FAMILY: case/space/underscore-insensitive, every OFFER* identifier
// is the one "Exclusive Fare", both corporate spellings are "Corporate", and the
// admin's legacy COUPON is TripJack's COUPONFARE.
const FARE_FAMILY_ALIAS = { CORPORATE_FARE: 'CORPORATE', COUPON: 'COUPONFARE' };
export function fareFamilyKey(v) {
  const k = String(v || '').trim().toUpperCase().replace(/[\s_-]+/g, '_');
  if (k.startsWith('OFFER')) return 'OFFER';
  return FARE_FAMILY_ALIAS[k] || k;
}

// ── Domestic / International ──────────────────────────────────────────────────
// Mirrors booking_backend/utils/domIntScope.js. "Domestic" is within India; a
// trip is international as soon as any segment touches an airport outside it
// (DEL→BOM→DXB is international). Unknown when a segment names no country, and
// a rule scoped to one of the two never fits an unknown trip.
const HOME_COUNTRY_KEYS = new Set(['IN', 'IND', 'INDIA']);
function countryDomInt(codeOrName) {
  const k = String(codeOrName || '').trim().toUpperCase();
  if (!k) return null;
  return HOME_COUNTRY_KEYS.has(k) ? 'domestic' : 'international';
}
const endCountry = (end) => end?.countryCode || end?.country || '';

/** Domestic / international for a list of TripJack segments (`sI`). */
export function classifySegments(segments) {
  const ends = [];
  for (const s of Array.isArray(segments) ? segments : []) {
    ends.push(countryDomInt(endCountry(s?.da)), countryDomInt(endCountry(s?.aa)));
  }
  if (ends.includes('international')) return 'international';
  if (ends.length && ends.every((e) => e === 'domestic')) return 'domestic';
  return null;
}

/** The whole trip from its legs' own answers: any international leg wins. */
export function tripDomInt(legs) {
  const list = (Array.isArray(legs) ? legs : []).filter(Boolean).map((l) => l.domInt || null);
  if (list.includes('international')) return 'international';
  if (list.length && list.every((d) => d === 'domestic')) return 'domestic';
  return null;
}

/** A rule's stored scope, normalised. null = every trip. */
export function ruleDomInt(rule) {
  const v = String(rule?.domInt ?? rule?.dom_int ?? '').trim().toLowerCase();
  return v === 'domestic' || v === 'international' ? v : null;
}

/**
 * Why a rule's Domestic / International scope refuses this trip — the scope it
 * wants ('domestic' | 'international') — or null when it does not refuse it.
 * Lets a refused coupon say "domestic trips only" rather than a generic line.
 */
export function domIntMiss(rule, legs) {
  const want = ruleDomInt(rule);
  if (!want) return null;
  return tripDomInt(legs) === want ? null : want;
}

/**
 * The chosen trip as the rules see it: one entry per leg, its marketing airline,
 * its fare family and whether it is domestic or international. `selectedLegs`
 * for a round trip / multi-city, else the one `selectedTrip` — the same legs the
 * server reads off its review.
 */
export function tripLegsFor(selectedLegs, selectedTrip) {
  const list = (Array.isArray(selectedLegs) && selectedLegs.length ? selectedLegs : [selectedTrip]).filter(Boolean);
  return list.map((l) => ({
    airlineCode: airlineOf(l?._raw?.sI?.[0]?.fD?.aI?.code || l?.airlineCode || l?.code || ''),
    fareIdentifier: String(l?.fareIdentifier || l?.fareIdentifer || '').trim(),
    domInt: classifySegments(l?._raw?.sI),
  }));
}

/**
 * Does a rule's scope fit the trip? Reads the camelCase shape /flight-offers
 * sends and the snake_case row /check-coupon returns. Unscoped fits everything;
 * a scoped rule must fit EVERY leg, and fits nothing when the trip is unknown.
 */
export function ruleFitsTrip(rule, legs) {
  if (!rule) return false;
  const airlineWise = (rule.airlineType ?? rule.airline_type) === 'airline_wise';
  const ruleAirline = airlineOf(rule.airlineCode ?? rule.airline_code);
  const ruleFare = fareFamilyKey(rule.fareType ?? rule.fare_type);
  if (!airlineWise && !ruleFare && !ruleDomInt(rule)) return true;
  const list = Array.isArray(legs) ? legs.filter(Boolean) : [];
  if (!list.length) return false;
  // Domestic / International is judged on the WHOLE trip, not leg by leg.
  if (domIntMiss(rule, list)) return false;
  return list.every((l) =>
    (!airlineWise || (ruleAirline && airlineOf(l.airlineCode) === ruleAirline))
    && (!ruleFare || fareFamilyKey(l.fareIdentifier) === ruleFare));
}

/** Airline beats none, fare type beats none, Dom/Int beats neither — the markup engine's precedence. */
function scopeSpecificity(rule) {
  const airlineWise = (rule?.airlineType ?? rule?.airline_type) === 'airline_wise';
  return (airlineWise ? 4 : 0)
    + (fareFamilyKey(rule?.fareType ?? rule?.fare_type) ? 2 : 0)
    + (ruleDomInt(rule) ? 1 : 0);
}

/**
 * The ONE convenience fee rule that applies to this trip: the most specific
 * fitting rule, the newest breaking a tie (the list arrives newest-first) —
 * flightPricingService.getActiveConvenienceFee. An older backend sends only the
 * single `convenienceFee`, which is used as-is.
 */
export function pickConvenienceFee(offers, legs) {
  const list = offers?.convenienceFees;
  if (!Array.isArray(list)) return offers?.convenienceFee || null;
  let best = null;
  for (const r of list) {
    if (!ruleFitsTrip(r, legs)) continue;
    if (!best || scopeSpecificity(r) > scopeSpecificity(best)) best = r;
  }
  return best;
}

/** The scope fields a stored coupon keeps, so a later step can re-judge it. */
export const couponScopeOf = (row) => ({
  airlineType: row?.airline_type || row?.airlineType || 'all',
  airlineCode: row?.airline_code || row?.airlineCode || null,
  fareType: row?.fare_type || row?.fareType || null,
  domInt: ruleDomInt(row),
});
